import { useState } from "react";
import { MEASURE_SLUGS } from "~/consts";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQuery,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    AssetTimeseriesColumns,
    AssetTimeseriesGroupBy,
} from "~/modules/asset_timeseries_v4/types";
import { MetricTypes } from ".";
import * as Analytics from "~/modules/visualization/analytics";

// // // //

interface MetricsProps {
    options: MetricTypes[];
}

interface MetricTimeseriesControlsProps {
    groupBy: AssetTimeseriesGroupBy;
    filters: Filter[];
    metrics: MetricsProps;
    analyticsChartName: string;
    children: (childProps: {
        appliedQuery: ApiQuery;
        metric: {
            value: MetricTypes;
            onChange: (value: MetricTypes) => void;
        };
        ignoreDownloadsFeatureFlag: boolean;
    }) => React.ReactNode;
}

/**
 * MetricTimeseriesControls
 * Manages state and query building for YieldTimeseriesChart visualization controls.
 * Handles metric selection for APY data.
 */
export function MetricTimeseriesControls(props: MetricTimeseriesControlsProps) {
    const { metrics } = props;

    const [selectedMetric, setSelectedMetric] = useState<MetricTypes>(
        metrics.options[0],
    );

    // Handler functions with analytics tracking
    const handleMetricChange = (nextValue: MetricTypes) => {
        Analytics.trackChangeMetricTimeseriesMetric({
            chartName: props.analyticsChartName,
            prevValue: selectedMetric,
            nextValue: nextValue,
        });
        setSelectedMetric(nextValue);
    };

    // Build the applied query based on the selected metric
    // Use Avg for APY metrics, Sum for Hypothetical10KPerformance
    const aggregateFunction =
        selectedMetric === MetricTypes.Apy7Day ||
        selectedMetric === MetricTypes.Apy30Day
            ? AggregateFunctions.Avg
            : AggregateFunctions.Sum;

    const baseQuery: ApiQuery = {
        sort: {
            direction: SortDirections.asc,
            field: AssetTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
        aggregate: {
            groupBy: props.groupBy,
            aggregateFunction: aggregateFunction,
            interval: AggregateQueryInterval.day,
        },
    };

    const METRIC_TO_MEASURE_ID: Record<MetricTypes, string> = {
        [MetricTypes.Hypothetical10KPerformance]:
            MEASURE_SLUGS.hypothetical_10_000_performance,
        [MetricTypes.Apy7Day]: MEASURE_SLUGS.apy_7_day,
        [MetricTypes.Apy30Day]: MEASURE_SLUGS.apy_30_day,
    };

    const metricFilter: Filter = {
        operator: FilterOperators.Equals,
        field: AssetTimeseriesColumns.measureSlug,
        value: METRIC_TO_MEASURE_ID[selectedMetric],
    };

    const filters: Filter[] = [metricFilter, ...props.filters];

    baseQuery.filter = {
        operator: FilterOperators.And,
        filters: filters,
    };

    // Allow downloads for APY metrics
    const ignoreDownloadsFeatureFlag = true;

    return (
        <>
            {props.children({
                appliedQuery: baseQuery,
                metric: {
                    value: selectedMetric,
                    onChange: handleMetricChange,
                },
                ignoreDownloadsFeatureFlag,
            })}
        </>
    );
}
